import { Chip, ChipGroup, OptionRow } from '../../primitives';
import {
  StepCta,
  StepHeading,
  StepStack,
  StepSub,
} from '../../OnboardingShell/StepLayout';
import { useOnboardingV2 } from '../../OnboardingShell/onboardingV2';
import {
  DISCIPLINE_OPTIONS,
  GOAL_EVENT_CHIPS,
  HEALTH_FREQ_OPTIONS,
  HEALTH_MOTIVATION_CHIPS,
  SOCIAL_FREQUENCY_CHIPS,
  SOCIAL_GROUP_OPTIONS,
  TIME_GOAL_DISTANCE_CHIPS,
  TIME_GOAL_TYPES,
  type HealthFreqId,
  type JourneyId,
  type SocialGroupId,
  type TimeGoalType,
} from '../../../fixtures/onboarding';
import type { OnboardingAnswers } from '../../../hooks/onboardingReducer';
import { ONBOARDING_CTA } from '../../../lib/onboardingCtaCopy';
import styles from './Step6Focus.module.css';

type Step6Props = {
  answers: OnboardingAnswers;
  setAnswer: (patch: Partial<OnboardingAnswers>) => void;
  onContinue: () => void;
  /**
   * B45 — the forward-CTA label, computed by the route (the only place that
   * knows whether the training-data beat or the guardian beat still stands
   * between this step and the coaches reveal). Defaults to "Continue": a step
   * may never announce the coaches on its own authority, so an omitted prop
   * fails quiet rather than promising the reveal early.
   */
  ctaLabel?: string;
};

/**
 * Step 6 — Focus (BRANCHED by journey, 4 variants).
 *
 * The journey-aware Step 6 is the structural payoff of moving Journey
 * to Step 1 — each runner type gets the questions that actually shape
 * their plan.
 *
 *   competitive_solo / competitive_team → Discipline + optional goal event
 *   time_goal                            → Goal type (4 intents) + optional distance
 *   health_fitness                       → Frequency + multi-select motivations
 *                                          (NO "Weight management" chip — RED-S policy)
 *   social                               → Group type + optional frequency
 *
 * All four variants share ONE forward-CTA label, and they no longer choose it.
 *
 * B45 — they used to hardcode "Meet my coaches", on the assumption recorded in
 * this comment that step 6 was the last substantive step before Welcome. F-520
 * later inserted the training-data beat between step 6 and Welcome, so for a
 * fresh signup the button announced the coaches reveal with one to three
 * questions still to come (founder reported twice, on device). The label now
 * arrives from the route via `ctaLabel` and reflects what actually comes NEXT.
 */
export function Step6Focus({
  answers,
  setAnswer,
  onContinue,
  ctaLabel = ONBOARDING_CTA.continue,
}: Step6Props) {
  const journey: JourneyId = answers.journey ?? 'health_fitness';

  const shared = { answers, setAnswer, onContinue, ctaLabel };

  if (journey === 'competitive_solo' || journey === 'competitive_team') {
    return <Step6Competitive {...shared} />;
  }
  if (journey === 'time_goal') {
    return <Step6TimeGoal {...shared} />;
  }
  if (journey === 'social') {
    return <Step6Social {...shared} />;
  }
  return <Step6Health {...shared} />;
}

/** Variant props — `ctaLabel` is resolved by the parent, never defaulted again. */
type Step6VariantProps = Step6Props & { ctaLabel: string };

// ──────────────────────────────────────────────────────────────────────────
// Competitive (competitive_solo / competitive_team)
// ──────────────────────────────────────────────────────────────────────────

function Step6Competitive({ answers, setAnswer, onContinue, ctaLabel }: Step6VariantProps) {
  const ready = !!answers.discipline;

  return (
    <>
      <StepHeading>Your running focus</StepHeading>
      <StepSub>
        Discipline shapes your weekly mileage and intensity mix. A goal
        event gives your plan a target. Leave it blank if you&rsquo;re
        between races.
      </StepSub>

      <div className={styles.sectionLabel}>Primary discipline (required)</div>
      <StepStack>
        {DISCIPLINE_OPTIONS.map((d) => (
          <OptionRow
            key={d.id}
            label={d.label}
            selected={answers.discipline === d.id}
            onClick={() => setAnswer({ discipline: d.id })}
          />
        ))}
      </StepStack>

      <div className={styles.sectionLabel}>Goal event (optional)</div>
      <div className={styles.touchChips}>
<ChipGroup>
        {GOAL_EVENT_CHIPS.map((g) => (
          <Chip
            key={g}
            label={g}
            selected={answers.goalEvent === g}
            onClick={() => setAnswer({ goalEvent: answers.goalEvent === g ? null : g })}
          />
        ))}
      </ChipGroup>
</div>

      <div style={{ height: 24 }} />
      <StepCta onClick={onContinue} disabled={!ready}>
        {ctaLabel}
      </StepCta>
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Time goal
// ──────────────────────────────────────────────────────────────────────────

function Step6TimeGoal({ answers, setAnswer, onContinue, ctaLabel }: Step6VariantProps) {
  const ready = !!answers.timeGoalType;

  return (
    <>
      <StepHeading>What&rsquo;s your goal?</StepHeading>
      <StepSub>We&rsquo;ll build your plan around this target.</StepSub>

      <StepStack>
        {TIME_GOAL_TYPES.map((g) => (
          <OptionRow
            key={g.id}
            label={g.label}
            sub={g.description}
            selected={answers.timeGoalType === g.id}
            onClick={() => setAnswer({ timeGoalType: g.id as TimeGoalType })}
          />
        ))}
      </StepStack>

      {/* Distance chips revealed after a goal type is selected, with fadeUp */}
      {answers.timeGoalType && (
        <div className={styles.fadeIn}>
          <div className={styles.sectionLabel}>Which distance? (optional)</div>
          <div className={styles.touchChips}>
<ChipGroup>
            {TIME_GOAL_DISTANCE_CHIPS.map((d) => (
              <Chip
                key={d}
                label={d}
                selected={answers.timeGoalDist === d}
                onClick={() => setAnswer({ timeGoalDist: answers.timeGoalDist === d ? null : d })}
              />
            ))}
          </ChipGroup>
</div>
        </div>
      )}

      <div style={{ height: 24 }} />
      <StepCta onClick={onContinue} disabled={!ready}>
        {ctaLabel}
      </StepCta>
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Health (health_fitness)
// ──────────────────────────────────────────────────────────────────────────

function Step6Health({ answers, setAnswer, onContinue, ctaLabel }: Step6VariantProps) {
  // Part 7 (P9): one chip style, so the health chips lose their green accent.
  const v2 = useOnboardingV2();
  const ready = !!answers.healthFreq;
  const motives = answers.healthMotives ?? [];

  const toggleMotive = (m: string) => {
    const next = motives.includes(m) ? motives.filter((x) => x !== m) : [...motives, m];
    setAnswer({ healthMotives: next });
  };

  return (
    <>
      <StepHeading>How does running fit into your week?</StepHeading>
      <StepSub>
        No right answer: this helps your coaches meet you where you are.
      </StepSub>

      <div className={styles.sectionLabel}>Current frequency (required)</div>
      <StepStack>
        {HEALTH_FREQ_OPTIONS.map((f) => (
          <OptionRow
            key={f.id}
            label={f.label}
            sub={f.description}
            selected={answers.healthFreq === f.id}
            onClick={() => setAnswer({ healthFreq: f.id as HealthFreqId })}
          />
        ))}
      </StepStack>

      <div className={styles.sectionLabel}>What motivates you? (optional, multi-select)</div>
      <div className={styles.touchChips}>
<ChipGroup>
        {HEALTH_MOTIVATION_CHIPS.map((m) => (
          <Chip
            key={m}
            label={m}
            selected={motives.includes(m)}
            onClick={() => toggleMotive(m)}
            accent={v2 ? undefined : 'var(--c-recovery)'}
            accentSoft={v2 ? undefined : 'rgba(15,122,90,0.08)'}
          />
        ))}
      </ChipGroup>
</div>

      <div style={{ height: 24 }} />
      <StepCta onClick={onContinue} disabled={!ready}>
        {ctaLabel}
      </StepCta>
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Social
// ──────────────────────────────────────────────────────────────────────────

function Step6Social({ answers, setAnswer, onContinue, ctaLabel }: Step6VariantProps) {
  const ready = !!answers.socialGroup;

  return (
    <>
      <StepHeading>Tell us about your running crew</StepHeading>
      <StepSub>
        Your coaches factor in group dynamics and coordination.
      </StepSub>

      <div className={styles.sectionLabel}>Who you run with (required)</div>
      <StepStack>
        {SOCIAL_GROUP_OPTIONS.map((g) => (
          <OptionRow
            key={g.id}
            label={g.label}
            sub={g.description}
            selected={answers.socialGroup === g.id}
            onClick={() => setAnswer({ socialGroup: g.id as SocialGroupId })}
          />
        ))}
      </StepStack>

      <div className={styles.sectionLabel}>How often together? (optional)</div>
      <div className={styles.touchChips}>
<ChipGroup>
        {SOCIAL_FREQUENCY_CHIPS.map((f) => (
          <Chip
            key={f}
            label={f}
            selected={answers.socialFreq === f}
            onClick={() => setAnswer({ socialFreq: answers.socialFreq === f ? null : f })}
          />
        ))}
      </ChipGroup>
</div>

      <div style={{ height: 24 }} />
      <StepCta onClick={onContinue} disabled={!ready}>
        {ctaLabel}
      </StepCta>
    </>
  );
}
